import React, { useEffect, useRef, useState } from 'react';
import Proptypes from 'prop-types';
import cn from 'classnames';

import debounce from 'lodash/debounce';

import LearnosityDistractor from 'legacy/src/components/learnosity/LearnosityDistractor';
import TEIDistractor from 'legacy/src/components/learnosity/TEIDistractor';
import { extractHtmlContent } from 'legacy/src/utility/utils';

import {
  findResponseIndex,
  hasContent,
  hasLearnosityQuestionAllAnswersCorrect,
} from './utils/helpers';
import MCQOptions from './MCQOptions';
import SlideToggle from 'legacy/src/utility/SlideToggle';

import { TEI_TYPES } from 'legacy/src/constants/assignment';

/**
 * Renders Learnosity question with options inline, renders distractors at the bottom with ability to
 * select response option and appropriate distractor became visible
 */
const LearnosityItemPreview = ({
  reference,
  isCurrent,
  learnosityQuestion,
  learnosityItem,
  areOptionsSelectable,
  responseAnalysis,
  studentResponsesForItem = null,
  assignmentReport,
  students,
  studentUrl,
  isMCQ,
  isStudent,
  studentId,
  showAnalysis,
  showOptions,
  announceOptions,
  overrideResponseIndex,
  disableShowCorrectAnswers,
  disableResize,
  isExpanded,
  isBluebook,
}) => {
  const [distractors, setDistractors] = useState([]);
  const [responseIndex, setResponseIndex] = useState(null);
  const [validResponseIndexes, setValidResponseIndexes] = useState([]);
  const [isCorrectOption, setCorrectOption] = useState(true);
  const [possibleOptions, setPossibleOptions] = useState([]);
  const [optionsToRender, setOptionsToRender] = useState([]);
  const [isMultipleResponse, setIsMultipleResponse] = useState(false);
  const [isTEI, setIsTEI] = useState(false);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [validResponses, setValidResponses] = useState(null);
  const [isClozeText, setIsClozeText] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [hasResized, setHasResized] = useState(false);
  const currentElement = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setShowCorrectAnswers(!disableShowCorrectAnswers);
  }, [disableShowCorrectAnswers, learnosityQuestion]);

  const itemColumns = document.querySelectorAll(`.learnosity-item .col-xs-6`);
  if (itemColumns && itemColumns.length && !isBluebook) {
    itemColumns.forEach((column) => {
      column.classList.remove('col-xs-6');
      column.classList.add('col-xs-12');
      column.classList.add('col-sm-6');
    });
  }

  useEffect(() => {
    if (overrideResponseIndex >= 0) {
      setCorrectOption(validResponseIndexes.includes(overrideResponseIndex));
      setResponseIndex(overrideResponseIndex);
    }
  }, [overrideResponseIndex]);

  useEffect(() => {
    if (possibleOptions && possibleOptions.length) {
      setOptionsToRender(
        possibleOptions.map((option) => {
          const ariaLabelText = extractHtmlContent(option.label, true);
          return {
            text: option.label,
            value: option.value,
            ariaLabel: ariaLabelText,
          };
        }),
      );
    }
  }, [possibleOptions]);

  // make Learnosity able to select option and show distractors.
  useEffect(() => {
    if (learnosityQuestion) {
      const { validation, type } = learnosityQuestion.getQuestion();

      if (TEI_TYPES.includes(type)) {
        setIsTEI(true);
        setValidResponses(validation && validation.valid_response.value);
        setIsClozeText(type === 'clozetext');
      }

      const options =
        learnosityQuestion.getProcessedOptions &&
        learnosityQuestion.getProcessedOptions();
      const questionResponse = learnosityQuestion.getResponse();

      if (options) {
        const possibleDistractors =
          learnosityQuestion.getMetadata().distractor_rationale_response_level;
        const possibleCustomDistractors =
          learnosityQuestion.getMetadata()
            .custom_distractor_rationale_response_level;
        setDistractors(
          possibleDistractors || possibleCustomDistractors || distractors,
        );
      }

      if (!isStudent && !disableShowCorrectAnswers) {
        setShowCorrectAnswers(true);
      }

      // FY-9559: The following is a injection hack to improve accessibility in the learnosity markup we get.

      // get the container with the stimulus
      const stimulus = document.querySelector('.stimulus_reference');
      // inject the tabindex
      if (stimulus) {
        stimulus.setAttribute('tabindex', 0);
      }

      if (options) {
        setPossibleOptions(options);
        const validResponse = validation && validation.valid_response;
        const questionResponseIndex =
          questionResponse && findResponseIndex(questionResponse, options);
        const correctResponseIndexes = validResponse.value.map((value) =>
          options.findIndex((option) => option.value === value),
        );
        if (responseIndex !== null) {
          setResponseIndex(questionResponseIndex);
        }
        setValidResponseIndexes(correctResponseIndexes);
        setIsMultipleResponse(
          !!learnosityQuestion.getQuestion().multiple_responses,
        );
        setIsLoading(false);
      }
    }
  }, [learnosityQuestion]);

  useEffect(() => {
    setCorrectOption(validResponseIndexes.includes(responseIndex));
  }, [responseIndex]);

  useEffect(() => {
    if (learnosityQuestion) {
      // isValid(withPartials = false), so we don't get partial results, just right or wrong.
      setIsCorrect(learnosityQuestion.isValid());

      // Hack around Learnosity injection method
      if (isTEI) {
        const possibleDistractors =
          learnosityQuestion.getMetadata().distractor_rationale_response_level;
        const possibleCustomDistractors =
          learnosityQuestion.getMetadata()
            .custom_distractor_rationale_response_level;
        setDistractors(
          possibleDistractors || possibleCustomDistractors || distractors,
        );
        const container = document.querySelector(
          `.learnosity-item[data-reference="${reference}"]`,
        );
        setIsCorrect(
          isCorrect || hasLearnosityQuestionAllAnswersCorrect(container),
        );
        container.className = `learnosity-item ${
          showCorrectAnswers ? '--visible-all' : '--hide-tei-response'
        } ${isStudent ? 'lrn_item_student' : 'lrn_item_teacher'}`;
      }
      if (!hasResized && !disableResize) {
        window.dispatchEvent(new Event('resize'));
        setHasResized(true);
      }
    }
  }, [isTEI, showCorrectAnswers]);

  const addRationaleToSelectedOption = debounce(() => {
    // get the container with the MCQs
    const mcqsContainer = document.querySelector('.lrn_mcqgroup');
    if (mcqsContainer) {
      // get the selected mcqOption
      const selectedMCQ = mcqsContainer.querySelector(
        '.lrn-mcq-option.--is-selected',
      );
      if (selectedMCQ) {
        // get the correct paragraph
        const paragraph = selectedMCQ.querySelector(
          '.lrn-possible-answer .choice_paragraph',
        );
        if (paragraph) {
          const paragraphText = paragraph.innerText || '';
          // parse the rationale
          const parsedRationale = extractHtmlContent(
            distractors[responseIndex],
            true,
          );
          // get the sr-only container
          const srOnlyContainer = selectedMCQ.querySelector('.sr-only');
          if (srOnlyContainer) {
            // we set the aria-live to 'polite' so that when the user selects the question, it gets re-read.
            srOnlyContainer.setAttribute('aria-live', 'polite');
            // set the new text
            srOnlyContainer.innerText = `${paragraphText} Rationale: ${
              parsedRationale || ''
            }`;
          }
        }
      }
    }
  }, 100);

  const resetRationaleForUnselectedOptions = debounce(() => {
    // get the container with the MCQs
    const mcqsContainer = document.querySelector('.lrn_mcqgroup');
    if (mcqsContainer) {
      const unselectedMCQs = mcqsContainer.querySelectorAll(
        '.lrn-mcq-option:not(.--is-selected)',
      );
      if (unselectedMCQs) {
        unselectedMCQs.forEach((mcq) => {
          const paragraph = mcq.querySelector(
            '.lrn-possible-answer .choice_paragraph',
          );
          if (paragraph) {
            const paragraphText = paragraph.innerText || '';
            const srOnlyContainer = mcq.querySelector('.sr-only');
            if (srOnlyContainer) {
              srOnlyContainer.innerText = paragraphText;
              srOnlyContainer.removeAttribute('aria-live');
            }
          }
        });
      }
    }
  }, 100);

  // FY-9559: Part 2
  if (learnosityQuestion) {
    if (showCorrectAnswers) {
      addRationaleToSelectedOption();
      resetRationaleForUnselectedOptions();
    }
  }

  const displayTeiDistractor =
    isTEI && showCorrectAnswers && validResponses && distractors;
  const displayUnattempted =
    isStudent &&
    isTEI &&
    !showCorrectAnswers &&
    learnosityQuestion &&
    !learnosityQuestion.isAttempted();

  return (
    <div>
      <div
        className={cn(
          { 'teacher-item-preview': isMCQ },
          `learnosity-typeset-${reference}`,
        )}
        hidden={!isCurrent}
        ref={currentElement}
      >
        <div>
          <div
            {...(announceOptions ? { 'aria-live': 'polite' } : {})}
            className="learnosity-item learnosity-item--hide-response question-preview-player"
            data-reference={reference}
            data-test-reference-visible={isCurrent}
          />
        </div>

        {showOptions && !isLoading && (
          <MCQOptions
            studentResponsesForItem={studentResponsesForItem}
            areOptionsSelectable={areOptionsSelectable}
            data-test-reference-visible={isCurrent}
            options={optionsToRender}
            distractors={distractors}
            correctIndexes={validResponseIndexes}
            chooseResponseIndex={setResponseIndex}
            responseAnalysis={responseAnalysis}
            assignmentReport={assignmentReport}
            students={students}
            studentUrl={studentUrl}
            studentId={studentId}
            currentQuestion={learnosityQuestion}
            currentItem={learnosityItem}
            showPerformance={!isStudent}
            isMultipleResponse={isMultipleResponse}
            showAnalysis={showAnalysis}
            announceOptions={announceOptions}
          />
        )}

        {displayTeiDistractor &&
          validResponses.map(
            (validResponse) =>
              distractors[validResponse] && (
                <TEIDistractor
                  key={validResponse}
                  className="tei-distractor"
                  text={distractors[validResponse]}
                />
              ),
          )}

        {isClozeText && showCorrectAnswers && distractors[0] && (
          <TEIDistractor className="tei-distractor" text={distractors[0]} />
        )}

        {displayUnattempted && (
          <TEIDistractor
            className="tei-distractor unattempted"
            text="Unattempted"
          />
        )}

        {isTEI && !isCorrect && (
          <div className="show-correct-answers">
            <span>Show the correct answer</span>
            <SlideToggle
              checked={showCorrectAnswers}
              onChange={setShowCorrectAnswers}
              offValue={false}
              onValue
              role="switch"
              data-ft-id="showResponse"
            />
          </div>
        )}

        {distractors.map(
          (distractor, index) =>
            hasContent(distractor) &&
            responseIndex !== null && (
              <LearnosityDistractor
                key={`${distractor}-${index}`}
                isVisible={responseIndex === index}
                isCorrectOption={isCorrectOption}
                index={responseIndex}
                html={distractor}
                isExpanded={isExpanded}
              />
            ),
        )}
      </div>
    </div>
  );
};

LearnosityItemPreview.displayName = 'LearnosityItemPreview';

LearnosityItemPreview.propTypes = {
  // Learnosity question Object
  // https://reference.learnosity.com/questions-api/index
  learnosityQuestion: Proptypes.object,
  // Flag for enabling the user to select question's options.
  areOptionsSelectable: Proptypes.bool,
  showAnalysis: Proptypes.bool,
  //Flag for rendering the options
  showOptions: Proptypes.bool,
  announceOptions: Proptypes.bool,
  /**
   * Overrides the response index
   * */
  overrideResponseIndex: Proptypes.number,
  disableShowCorrectAnswers: Proptypes.bool,
  disableResize: Proptypes.bool,
  isBluebook: Proptypes.bool,
};

LearnosityItemPreview.defaultProps = {
  learnosityQuestion: null,
  isYourResult: false,
  areOptionsSelectable: false,
  showAnalysis: true,
  showOptions: true,
  announceOptions: true,
  overrideResponseIndex: null,
  disableShowCorrectAnswers: false,
  disableResize: false,
  shouldHideParts: false,
  isBluebook: false,
};

export default LearnosityItemPreview;
